import React from 'react';
import {Link, browserHistory} from 'react-router';
import Alert from '../Alert';
import FilterButton from '../FilterButton';
import Icon from '../Icon';
import LinkComp from '../LinkComp';
import ListResults from '../ListResults';
import ListStats from '../ListStats';
import svgSortAsc from '../../svgs/arrow-up.svg';
import svgSortDesc from '../../svgs/arrow-down.svg';
import dollars from '../../dollar-format';
import {dateISOtoUS} from '../../date-converter';
import {getCurrentAccess} from '../../access';
import {canLinkToProperty} from '../../access-levels';
import {propertyGroupApi} from '../../requests/api';
import {fetchActiveBuyersTile} from '../../requests/investors';
import {
	GROUP_ORDER_KEY,
	buildListQueries,
	fetchInvestorHotListCount,
	fetchOwnerResearchReviews,
	fetchStatsNames,
	fetchStatsResults,
	listQueryOf,
	statsFromResults,
} from './listQueries';

const LIMIT = 100;
const DEFAULT_ORDER = 'property_count.desc';

const COLUMNS = [
	{code: 'name', name: 'Group Name', render: (g) => g.name},
	{code: 'property_count', name: 'Properties', render: (g) => g.property_count},
	{code: 'matching_count', name: 'Matching', render: (g) => g.matching_count},
	{name: 'Latest Status', render: (g) => g.latest_status},
	{code: 'last_close_of_escrow', name: 'Last Closed', render: (g) => dateISOtoUS(g.last_close_of_escrow) || ''},
];

const percent = (value) => (value === undefined || value === null || value === '' ? '' : Number(value).toFixed(2) + '%');

function PropertyLink({property, children}) {
	const to = `${arc.path.base}property/${property.id}`;
	// Same rule as the properties list: members without access to the record
	// get the public page instead of the in-app one.
	return canLinkToProperty(getCurrentAccess(), {SOURCE: property.source, STATUS: property.status}) ? (
		<LinkComp className='green' to={{pathname: to}}>{children}</LinkComp>
	) : (
		<a href={`https://www.iclimb.io/property/${property.id}`} target='_blank'>{children}</a>
	);
}

// Without filters every property matches, so highlighting would only tint
// every row; it marks something only once a filter narrows the list.
function MemberTable({properties, highlight}) {
	return (
		<table className='PropertyGroupList_members'>
			<thead>
				<tr>
					{['ID', 'Name', 'Status', 'Transaction Type', 'Source', 'Listed', 'Closed', 'List Price', 'Sold Price', 'Cap Rate', 'Listing Agent'].map((name) => <th key={name}>{name}</th>)}
				</tr>
			</thead>
			<tbody>
				{properties.map((p) => (
					<tr key={p.id} className={highlight && p.matches ? 'PropertyGroupList_match' : ''}>
						<td><PropertyLink property={p}>{p.id}</PropertyLink></td>
						<td><PropertyLink property={p}>{p.name || 'Untitled'}</PropertyLink></td>
						<td>{p.status}</td>
						<td>{p.transaction_type}</td>
						<td>{p.source}</td>
						<td>{dateISOtoUS(p.listed_date) || ''}</td>
						<td>{dateISOtoUS(p.close_of_escrow) || ''}</td>
						<td>{dollars(p.list_price)}</td>
						<td>{dollars(p.sold_price)}</td>
						<td>{percent(p.close_cap_rate)}</td>
						<td>{p.listing_agent_name}</td>
					</tr>
				))}
			</tbody>
		</table>
	);
}

export default class PropertyGroupList extends React.Component {
	constructor(props) {
		super(props);
		this.requestId = 0;
		this.statsRequestId = 0;
		this.statsSignature = null;
		this.listingRef = React.createRef();
		this.headerRef = React.createRef();
		this.updateHeaderOffset = this.updateHeaderOffset.bind(this);
		this.state = {
			groups: null,
			total: false,
			error: null,
			isBusy: null,
			expanded: {},
			displayDiv: false,
			query: buildListQueries(props.location.query, props.userData).query,
			stats: null,
			territoryFlagCounts: null,
			ownerResearchReviews: null,
			activeBuyers: null,
			investorHotListCount: 0,
			names: {agentName: null, territoryName: null, productTypeName: null, marketName: null},
		};
	}

	componentDidMount() {
		window.scrollTo(0, 0);
		// The corner piece under the header shows once the table has settled, as SortTable does.
		this.cornerTimer = setTimeout(() => this.setState({displayDiv: true}), 1000);
		this.load();
		this.loadStats();
		// Stagger the extra requests so they do not pile up on the stats ones.
		this.extrasTimer = setTimeout(() => this.loadExtras(), 100);

		this.updateHeaderOffset();
		window.addEventListener('resize', this.updateHeaderOffset);
		if (typeof ResizeObserver !== 'undefined' && this.headerRef.current) {
			this.headerResizeObserver = new ResizeObserver(() => this.updateHeaderOffset());
			this.headerResizeObserver.observe(this.headerRef.current);
		}
	}

	componentDidUpdate(prevProps) {
		const prev = prevProps.location.query;
		const next = this.props.location.query;
		if (JSON.stringify(prev) !== JSON.stringify(next)) {
			window.scrollTo(0, 0);
			this.load();
			this.loadStats();
			if (JSON.stringify(listQueryOf(prev)) !== JSON.stringify(listQueryOf(next))) this.loadExtras();
		}
	}

	componentWillUnmount() {
		// Bumps the counters so responses still in flight are dropped.
		this.unmounted = true;
		this.requestId++;
		this.statsRequestId++;
		clearTimeout(this.cornerTimer);
		clearTimeout(this.extrasTimer);
		window.removeEventListener('resize', this.updateHeaderOffset);
		if (this.headerResizeObserver) this.headerResizeObserver.disconnect();
	}

	// The sticky table header sits flush below the page header, whose height
	// changes with the stats boxes; see Listing.scss.
	updateHeaderOffset() {
		const header = this.headerRef.current;
		const root = this.listingRef.current;
		if (!header || !root) return;
		root.style.setProperty('--properties-header-offset', header.offsetHeight + 'px');
	}

	load() {
		const query = this.props.location.query;
		const plan = buildListQueries(query, this.props.userData);
		const id = ++this.requestId;
		this.setState({isBusy: true, query: plan.query});
		propertyGroupApi.search({
			query_name: plan.propertiesQueryName,
			options: plan.options,
			order: query[GROUP_ORDER_KEY] || DEFAULT_ORDER,
			page: query.page || 1,
			per_page: LIMIT,
		}).then((payload) => {
			if (id !== this.requestId) return;
			const pagination = (payload.meta && payload.meta.pagination) || {};
			this.setState({
				groups: payload.data || [],
				total: pagination.total === undefined ? (payload.data || []).length : pagination.total,
				error: null,
				isBusy: null,
				expanded: {},
			});
		}).catch((err) => {
			if (id !== this.requestId) return;
			this.setState({error: err.message || 'Could not load property groups.', isBusy: null});
		});
	}

	// The stats only change with the filters, so a re-sort or a page step leaves them be.
	loadStats() {
		const plan = buildListQueries(this.props.location.query, this.props.userData);
		if (plan.signature === this.statsSignature) return;
		this.statsSignature = plan.signature;
		const id = ++this.statsRequestId;
		fetchStatsResults(plan, this.props.userData).then((values) => {
			if (id !== this.statsRequestId) return;
			this.setState(statsFromResults(values));
		}).catch(() => {
			if (id !== this.statsRequestId) return;
			this.statsSignature = null;
			this.setState({stats: null, territoryFlagCounts: null});
		});
	}

	loadExtras() {
		const query = listQueryOf(this.props.location.query);
		const signature = JSON.stringify(query);
		this.extrasSignature = signature;
		const current = () => this.extrasSignature === signature && !this.unmounted;
		fetchInvestorHotListCount(this.props.userData).then((investorHotListCount) => {
			if (current()) this.setState({investorHotListCount});
		});
		fetchOwnerResearchReviews(query).then((ownerResearchReviews) => {
			if (current()) this.setState({ownerResearchReviews});
		});
		fetchActiveBuyersTile(query).then((activeBuyers) => {
			if (current()) this.setState({activeBuyers});
		});
		fetchStatsNames(query).then((names) => {
			if (current()) this.setState({names});
		});
	}

	toggle(id) {
		const expanded = {...this.state.expanded};
		if (expanded[id]) delete expanded[id];
		else expanded[id] = true;
		this.setState({expanded});
	}

	toggleAll() {
		const groups = this.state.groups || [];
		const allOpen = groups.length > 0 && groups.every((g) => this.state.expanded[g.id]);
		const expanded = {};
		if (!allOpen) groups.forEach((g) => { expanded[g.id] = true; });
		this.setState({expanded});
	}

	onRowClick(e, id) {
		// Links and buttons inside the row do their own thing.
		if (e.target.closest('a, button, input')) return;
		this.toggle(id);
	}

	sortQuery(code) {
		const query = {...this.props.location.query};
		const current = (query[GROUP_ORDER_KEY] || DEFAULT_ORDER).split('.');
		delete query.page;
		if (current[0] !== code) query[GROUP_ORDER_KEY] = code + '.asc';
		else if (current[1] === 'asc') query[GROUP_ORDER_KEY] = code + '.desc';
		else delete query[GROUP_ORDER_KEY];
		return query;
	}

	// The same markup as a SortTable header cell.
	renderHeader(col) {
		if (!col.code) {
			return (
				<th key={col.name} className='padding-left padding-right'>
					<div className='th'>{col.name}</div>
				</th>
			);
		}
		const [code, direction] = (this.props.location.query[GROUP_ORDER_KEY] || DEFAULT_ORDER).split('.');
		const isActive = code === col.code;
		return (
			<th className={`${isActive ? 'SortTable_th--active' : ''} padding-left padding-right`} key={col.code}>
				<Link to={{pathname: this.props.location.pathname, query: this.sortQuery(col.code)}}>
					<div className={`SortTable_th_name ${isActive ? 'active' : ''}`}>
						<span className={`SortTable_th_name_icon top ${isActive && direction === 'asc' ? 'active' : ''}`}>
							<Icon svg={svgSortAsc}/>
						</span>
						{col.name}
						<span className={`SortTable_th_name_icon bottom ${isActive && direction === 'desc' ? 'active' : ''}`}>
							<Icon svg={svgSortDesc}/>
						</span>
					</div>
				</Link>
			</th>
		);
	}

	render() {
		const {groups, total, error, isBusy, expanded, displayDiv, query, stats, names} = this.state;
		const {location} = this.props;
		const allOpen = !!groups && groups.length > 0 && groups.every((g) => expanded[g.id]);
		const filters = listQueryOf(location.query);
		const hasFilters = Object.keys(filters).some((key) => key !== 'page');

		return (
			<div className='Listing PropertyGroupList' data-is-busy={isBusy} ref={this.listingRef}>
				<div className='page-rounded-corner-container' ref={this.headerRef}>
					<div className='page-header-container list'>
						<div style={{display: 'flex', alignItems: 'center'}}>
							<ListStats
								stats={stats}
								query={query}
								uniquePropCount={0}
								items={[]}
								territoryFlagCounts={this.state.territoryFlagCounts}
								ownerResearchReviews={this.state.ownerResearchReviews}
								activeBuyers={this.state.activeBuyers}
								investorHotListCount={this.state.investorHotListCount}
								agentName={names.agentName}
								productTypeName={names.productTypeName}
								territoryName={names.territoryName}
								marketName={names.marketName}
								onAgentClick={query.TERRITORY_LISTING_AGENT_TEAM ? () => {
									const initial = {...filters};
									delete initial.page;
									delete initial.NEW_DEVELOPMENT;
									delete initial.HOT_LIST;
									delete initial.HOT_LIST_USER_EMAIL;
									delete initial.NEED_OWNER;
									browserHistory.push({pathname: '/list', query: initial});
								} : null}
							/>
						</div>
						<div className='page-header list' style={{flexDirection: 'column', alignItems: 'flex-start', height: 'auto'}}>
							<div className='page-title' style={{display: 'flex', alignItems: 'center', width: '100%'}}>
								Property Groups
								{total !== false && <span className='PropertyGroupList_count'>{Number(total).toLocaleString()}</span>}
							</div>
							<div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
								<FilterButton onClick={this.props.openFilter}/>
								<button
									type='button'
									className='ListMapToggle'
									onClick={() => {
										const listQuery = {...filters};
										delete listQuery.page;
										browserHistory.push({pathname: '/list', query: listQuery});
									}}
								>
									Properties
								</button>
							</div>
						</div>
						<div className='line'/>
						<div className='buttons-container PropertyGroupList_hint'>
							{hasFilters
								? 'Listed: buildings with at least one property matching the filters. Matching properties are highlighted.'
								: 'Every building with a live property. Apply a filter to highlight the matching properties.'}
						</div>
					</div>
				</div>
				<div className='page-body-container'>
					{error && <Alert type='error'>{error}</Alert>}
					<div className={`SortTable_rounded-corner properties${displayDiv ? '' : ' display-none'}`}/>
					<table className='SortTable SortTable--properties PropertyGroupList_table'>
						<thead>
							<tr>
								<th className='padding-left padding-right PropertyGroupList_toggle_th'>
									<div className='th'>
										<button
											type='button'
											className='PropertyGroupList_chevron'
											aria-expanded={allOpen}
											aria-label={allOpen ? 'Collapse all' : 'Expand all'}
											title={allOpen ? 'Collapse all' : 'Expand all'}
											onClick={() => this.toggleAll()}
											disabled={!groups || !groups.length}
										/>
									</div>
								</th>
								{COLUMNS.map((col) => this.renderHeader(col))}
							</tr>
						</thead>
						<tbody>
							{(groups || []).map((g) => {
								const open = !!expanded[g.id];
								return (
									<React.Fragment key={g.id}>
										<tr className={'PropertyGroupList_row' + (open ? ' open' : '')} onClick={(e) => this.onRowClick(e, g.id)}>
											<td className='PropertyGroupList_toggle_td padding-left padding-right'>
												<button type='button' className='PropertyGroupList_chevron' aria-expanded={open} aria-label={(open ? 'Collapse ' : 'Expand ') + g.name} onClick={() => this.toggle(g.id)}/>
											</td>
											{COLUMNS.map((col, i) => <td key={i} className='padding-left padding-right'>{col.render(g)}</td>)}
										</tr>
										{open && (
											<tr className='PropertyGroupList_sub'>
												<td colSpan={COLUMNS.length + 1}>
													<div className='PropertyGroupList_sub_scroll'>
														<MemberTable properties={g.properties || []} highlight={hasFilters}/>
													</div>
												</td>
											</tr>
										)}
									</React.Fragment>
								);
							})}
						</tbody>
					</table>
					<ListResults
						query={location.query}
						pathname={location.pathname}
						error={null}
						totalCount={total}
						itemCount={groups ? groups.length : false}
						originalItemCount={false}
						limit={LIMIT}
						className='property groups'
					/>
				</div>
			</div>
		);
	}
}
